import { useEffect, useRef } from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useAppStore } from '@/store/appStore'
import {
  getCachedContentHash,
  getPendingNoteChanges,
  clearOfflineQueue,
  saveAllNotes,
  getPendingBookmarkChanges,
  clearBookmarkOfflineQueue,
  saveAllBookmarks,
  getPendingFeedback,
  deleteQueuedFeedback,
  clearFeedbackOfflineQueue,
} from '@/db/viewerDb'
import { checkContentVersion } from '@/api/contentApi'
import { syncNotes } from '@/api/notesApi'
import { syncBookmarks } from '@/api/bookmarksApi'
import { submitFeedback } from '@/api/feedbackApi'
import type { ContentType } from '@/types/api'

export function useReconnectSync() {
  const { isOffline, selectedLanguage, bumpUserDataSyncVersion } = useAppStore()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const wasOffline = useRef(false)

  useEffect(() => {
    // Detect the transition from offline → online
    if (wasOffline.current && !isOffline) {
      wasOffline.current = false
      runReconnectSync()
    }
    if (isOffline) {
      wasOffline.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOffline])

  const runReconnectSync = async () => {
    try {
      // 1. Sync offline note queue
      const pendingNotes = await getPendingNoteChanges()
      if (pendingNotes.length > 0) {
        const response = await syncNotes({ changes: pendingNotes.map((p) => p.change) })
        await saveAllNotes(response.notes)
        await clearOfflineQueue()
        queryClient.invalidateQueries({ queryKey: ['notes'] })
      }

      // 2. Sync offline bookmark queue
      const pendingBookmarks = await getPendingBookmarkChanges()
      if (pendingBookmarks.length > 0) {
        const response = await syncBookmarks({ changes: pendingBookmarks.map((p) => p.change) })
        const mapped = response.bookmarks.map((b) => ({
          id: Number(b.id),
          contentType: b.contentType as ContentType,
          entityId: Number(b.entityId),
          createdOn: b.createdOn ?? null,
        }))
        await saveAllBookmarks(mapped)
        await clearBookmarkOfflineQueue()
        bumpUserDataSyncVersion()
      }

      // 3. Flush feedback composed while offline — same endpoint a live submission uses,
      // just with the whole queue as its items. Only entries the server confirms are dropped.
      const pendingFeedback = await getPendingFeedback()
      if (pendingFeedback.length > 0) {
        try {
          const response = await submitFeedback(pendingFeedback.map((p) => p.submission))
          const acceptedIds = new Set(response.accepted.map((a) => a.clientId))
          await deleteQueuedFeedback(
            pendingFeedback.filter((p) => acceptedIds.has(p.submission.clientId)).map((p) => p.key)
          )
        } catch (err) {
          // Any client-side rejection is terminal for this batch — feedback switched off (403)
          // or a malformed entry would be re-rejected forever, so drop the queue rather than
          // retry it on every reconnect. Server/network failures stay queued.
          if (axios.isAxiosError(err) && err.response && err.response.status < 500) {
            await clearFeedbackOfflineQueue()
            // The queue is gone, so this is the only chance to tell the user their entries were
            // not delivered. A server or network failure stays queued and retries silently.
            toast.error(t('feedback.syncFailed'))
          }
          console.warn('Feedback sync failed:', err)
        }
      }

      // 4. Check if content bundle is stale
      const cachedHash = await getCachedContentHash(selectedLanguage)
      const versionResponse = await checkContentVersion(selectedLanguage)
      if (cachedHash !== versionResponse.contentHash) {
        // Content has changed — invalidate the bundle query so it re-fetches
        queryClient.invalidateQueries({ queryKey: ['content-bundle', selectedLanguage] })
      }
    } catch (err) {
      // Non-fatal — sync will be retried next reconnection
      console.warn('Reconnect sync failed:', err)
    }
  }
}
